/**
 * API Service for Partner Access Management
 * Communicates with backend/Controllers/PartnerAccessController.cs
 */

import {
  AccessToken,
  GamePartnerAssignment,
  PartnerAccessAudit,
  CreatePartnerTokenRequest,
  CreatePartnerTokenResponse,
  CreatePartnerAccessRequest,
  CreatePartnerAccessResponse,
  UpdateAssignmentRequest,
  RevokeTokenRequest,
  RevokeAssignmentRequest,
  PartnerAccessStatsResponse
} from '../types/partnerAccess';
import { getToken } from '../utils/auth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}/api${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      console.error('Unauthorized! Token may be invalid or expired');
    }
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const partnerAccessApi = {
  // ==================== Token Management ====================

  /**
   * Create a new partner token (simplified - no game assignment)
   */
  createPartnerToken: async (request: CreatePartnerTokenRequest): Promise<CreatePartnerTokenResponse> => {
    console.log('partnerAccessApi.createPartnerToken: Sending request:', request);
    const response = await makeAuthenticatedRequest('/partner-access/tokens', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    console.log('partnerAccessApi.createPartnerToken: Raw response:', response);
    console.log('partnerAccessApi.createPartnerToken: Extracted data:', response.data);
    return response.data; // Extract data from ApiResponse wrapper
  },

  /**
   * DEPRECATED: Create a new partner access token with game assignment
   */
  createPartnerAccess: async (request: CreatePartnerAccessRequest): Promise<CreatePartnerAccessResponse> => {
    const response = await makeAuthenticatedRequest('/partner-access', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response.data;
  },

  /**
   * Get all access tokens
   */
  getAllTokens: async (includeRevoked = false): Promise<AccessToken[]> => {
    console.log('partnerAccessApi.getAllTokens: Sending request, includeRevoked =', includeRevoked);
    const response = await makeAuthenticatedRequest(`/partner-access/tokens?includeRevoked=${includeRevoked}`);
    console.log('partnerAccessApi.getAllTokens: Raw response =', response);
    console.log('partnerAccessApi.getAllTokens: response.data =', response.data);
    return response.data;
  },

  /**
   * Get token by ID
   */
  getTokenById: async (id: number): Promise<AccessToken> => {
    const response = await makeAuthenticatedRequest(`/partner-access/tokens/${id}`);
    return response.data;
  },

  /**
   * Revoke an access token (cascades to all assignments)
   */
  revokeToken: async (id: number, request: RevokeTokenRequest): Promise<void> => {
    await makeAuthenticatedRequest(`/partner-access/tokens/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // ==================== Assignment Management ====================

  /**
   * Get all game partner assignments
   */
  getAllAssignments: async (
    partnerId?: number,
    tokenId?: number,
    includeRevoked = false
  ): Promise<GamePartnerAssignment[]> => {
    const params = new URLSearchParams();
    if (partnerId != null) params.append('partnerId', partnerId.toString());
    if (tokenId) params.append('tokenId', tokenId.toString());
    params.append('includeRevoked', includeRevoked.toString());

    console.log('partnerAccessApi.getAllAssignments: Sending request with params:', params.toString());
    const response = await makeAuthenticatedRequest(`/partner-access/assignments?${params.toString()}`);
    console.log('partnerAccessApi.getAllAssignments: Raw response =', response);
    console.log('partnerAccessApi.getAllAssignments: response.data =', response.data);
    return response.data;
  },

  /**
   * Get assignment by ID
   */
  getAssignmentById: async (id: number): Promise<GamePartnerAssignment> => {
    const response = await makeAuthenticatedRequest(`/partner-access/assignments/${id}`);
    return response.data;
  },

  /**
   * Update an assignment
   */
  updateAssignment: async (id: number, request: UpdateAssignmentRequest): Promise<GamePartnerAssignment> => {
    const response = await makeAuthenticatedRequest(`/partner-access/assignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
    return response.data;
  },

  /**
   * Revoke a specific assignment
   */
  revokeAssignment: async (id: number, request: RevokeAssignmentRequest): Promise<void> => {
    await makeAuthenticatedRequest(`/partner-access/assignments/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // ==================== Audit & Statistics ====================

  /**
   * Get audit logs
   */
  getAuditLogs: async (
    partnerId?: number,
    tokenId?: number,
    startDate?: string,
    endDate?: string,
    limit = 100
  ): Promise<PartnerAccessAudit[]> => {
    const params = new URLSearchParams();
    if (partnerId != null) params.append('partnerId', partnerId.toString());
    if (tokenId) params.append('tokenId', tokenId.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('limit', limit.toString());

    const response = await makeAuthenticatedRequest(`/partner-access/audit?${params.toString()}`);
    return response.data;
  },

  /**
   * Get statistics
   */
  getStats: async (): Promise<PartnerAccessStatsResponse> => {
    const response = await makeAuthenticatedRequest('/partner-access/stats');
    return response.data;
  },

  /**
   * Create a partner invitation
   */
  createInvite: async (request: {
    email: string;
    partnerId: number;
    role?: string;
    expiryDays?: number;
  }): Promise<{
    inviteId: number;
    email: string;
    partnerId: number;
    partnerName: string;
    role: string;
    inviteToken: string;
    inviteLink: string;
    createdAt: string;
    expiresAt: string;
  }> => {
    const response = await makeAuthenticatedRequest('/partner-access/invite', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response.data;
  },

  // ==================== Partner User Management ====================

  /**
   * Get all partner users (admin view)
   */
  getPartnerUsers: async (includeInactive = false, partnerId?: number): Promise<PartnerUserAdmin[]> => {
    const params = new URLSearchParams();
    params.append('includeInactive', includeInactive.toString());
    if (partnerId != null) params.append('partnerId', partnerId.toString());
    const response = await makeAuthenticatedRequest(`/partner-access/users?${params.toString()}`);
    return response.data;
  },

  /**
   * Update a partner user (role, active status)
   */
  updatePartnerUser: async (id: string, request: { role?: string; isActive?: boolean }): Promise<PartnerUserAdmin> => {
    const response = await makeAuthenticatedRequest(`/partner-access/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
    return response.data;
  },

  /**
   * Delete a partner user permanently
   */
  deletePartnerUser: async (id: string): Promise<void> => {
    await makeAuthenticatedRequest(`/partner-access/users/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Reset a partner user's password (admin action)
   */
  resetPartnerUserPassword: async (id: string, newPassword: string): Promise<void> => {
    await makeAuthenticatedRequest(`/partner-access/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  },
};

// Types
export interface PartnerUserAdmin {
  id: string;
  email: string;
  displayName?: string;
  partnerId: number;
  partnerName?: string;
  role: string;
  isActive: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt?: string;
  createdBy?: string;
}

